/**
 * This was forked from metropolis
 * (https://github.com/SEEK-Jobs/metropolis/blob/6c4f8ee26fe56afbdd8c8b29d452180f18c31fa6/packages/multiselect-autosuggest/package.json#L1)
 * to add floating-ui, and then cleaned up a bit to make it look better, etc
 */

import { Box, Tag, Text } from 'braid-design-system';
import { useCombobox, useMultipleSelection } from 'downshift';
import { type ReactNode, useRef } from 'react';

import {
  autoUpdate,
  size as floatingSize,
  useFloating,
} from '@floating-ui/react';
import * as styles from './MultiselectAutosuggest.css';

interface BaseDownshiftItem {
  id: string;
  name: string;
}

interface MultiselectAutosuggestProps<T extends BaseDownshiftItem> {
  id: string;
  suggestions: T[];
  selectedItems: T[];
  searchText: string;
  setSearchText: (value: string) => void;
  placeholder?: string;
  onItemsSelected: (items: T[]) => void;
}

export const MultiselectAutosuggest = <T extends BaseDownshiftItem>({
  id,
  suggestions,
  selectedItems,
  placeholder,
  searchText,
  setSearchText,
  onItemsSelected,
}: MultiselectAutosuggestProps<T>): ReactNode => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { refs, floatingStyles } = useFloating({
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingSize({
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.min(Math.max(0, availableWidth), elements.reference.getBoundingClientRect().width)}px`,
            maxHeight: `${Math.max(0, availableHeight - 20)}px`,
          });
        },
      }),
    ],
  });

  const { getDropdownProps } = useMultipleSelection({
    selectedItems,
    onStateChange: ({ type, selectedItems: newSelectedItems }) => {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          onItemsSelected(newSelectedItems ?? []);
          break;
        default:
          break;
      }
    },
  });

  const {
    isOpen,
    highlightedIndex,
    getInputProps,
    getMenuProps,
    getItemProps,
    openMenu,
  } = useCombobox({
    items: suggestions,
    itemToString: (item) => (item ? item.id : ''),
    inputValue: searchText,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;

      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          const newChanges = {
            ...changes,
            isOpen: true, // keeps the menu open after selection.
            highlightedIndex: state.highlightedIndex,
          };
          return newChanges;
        case useCombobox.stateChangeTypes.InputClick:
          return {
            ...changes,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            const index = selectedItems.findIndex(
              (i) => i.id === selectedItem.id,
            );
            onItemsSelected(
              index === -1
                ? [...selectedItems, selectedItem]
                : selectedItems.filter((_, i) => i !== index),
            );
            setSearchText('');
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setSearchText(newInputValue ?? '');
          break;
        default:
          break;
      }
    },
  });

  const hasSelectedItems = selectedItems.length > 0;

  return (
    <Box
      id={`${id}-multiselect-autosuggest`}
      display="flex"
      flexDirection="column"
      position="relative"
      width="full"
    >
      <Box
        position="relative"
        background="surface"
        width="full"
        onClick={(e) => {
          openMenu();
          e.stopPropagation();
        }}
        className={styles.inputParent}
        ref={refs.setReference}
      >
        {selectedItems.map((item) => (
          <Box className={styles.tag} key={item.id}>
            <Tag
              size="small"
              clearLabel="Clear"
              onClear={() =>
                onItemsSelected(selectedItems.filter((i) => i.id !== item.id))
              }
            >
              {item.name}
            </Tag>
          </Box>
        ))}

        <input
          {...getInputProps(
            getDropdownProps({
              ref: inputRef,
              value: searchText,
              placeholder: hasSelectedItems ? '' : placeholder,
              className: `${styles.standardText} ${styles.input}`,
              display:
                isOpen ||
                searchText ||
                (!searchText && selectedItems.length === 0)
                  ? 'flex'
                  : 'hidden',
            }),
          )}
        />
      </Box>
      <Box
        {...getMenuProps({
          component: 'ul',
          display: isOpen ? 'block' : 'none',
          background: 'surface',
          borderRadius: 'standard',
          width: 'full',
          zIndex: 'dropdown',
          boxShadow: 'medium',
          position: 'absolute',
          className: styles.suggestionList,
          style: floatingStyles,
          ref: refs.setFloating,
        })}
      >
        {suggestions.map((suggestion, index) => {
          const isSelected = selectedItems.some(
            (selectedItem) => selectedItem.id === suggestion.id,
          );

          if (isSelected) {
            return null;
          }

          return (
            <Box
              key={suggestion.id}
              background={
                highlightedIndex === index ? 'formAccentSoftHover' : 'surface'
              }
              cursor="pointer"
              padding="small"
              {...getItemProps({
                item: suggestion,
              })}
            >
              <Text>{suggestion.name}</Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
